import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Sparkles, ArrowRight, Navigation, Gift, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { BACKEND_URL } from '../config';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    green: '#22c55e',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const NewUserOnboarding = ({ 
  redemptionResult = null, 
  businessName = null, 
  onComplete, 
  showSuccessFirst = true 
}) => {
  const [step, setStep] = useState(showSuccessFirst && redemptionResult ? 1 : 2);
  const [nearestHub, setNearestHub] = useState(null);
  const [loadingHub, setLoadingHub] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Find nearest venue
  const findNearestVenue = useCallback(async () => {
    setGettingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setGettingLocation(false);
        setLoadingHub(true);
        
        try {
          const { latitude, longitude } = position.coords;
          const response = await axios.get(
            `${BACKEND_URL}/api/hubs/nearest-with-deals?lat=${latitude}&lng=${longitude}`
          );
          
          if (response.data.error) {
            setLocationError(response.data.error);
          } else {
            setNearestHub(response.data);
          }
        } catch (err) {
          console.error('Error finding nearest venue:', err);
          setLocationError("Couldn't find venues near you");
        }
        
        setLoadingHub(false);
      },
      (error) => {
        setGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError("Location access denied. Please enable location services.");
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError("Location information unavailable.");
            break;
          case error.TIMEOUT:
            setLocationError("Location request timed out.");
            break;
          default:
            setLocationError("An unknown error occurred.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // Auto-find location when step 2 loads
  useEffect(() => {
    if (step === 2 && !nearestHub && !loadingHub) {
      findNearestVenue();
    }
  }, [step, nearestHub, loadingHub, findNearestVenue]);

  // Step 1: Success message (only for new signups who redeemed)
  const renderStep1 = () => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="text-center"
    >
      {/* Success Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
        className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
      >
        <CheckCircle2 className="w-12 h-12" style={{ color: v5.colors.green }} />
      </motion.div>
      
      <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
        // welcome_to_tappd
      </p>
      
      <h2 style={{ fontFamily: v5.fonts.display, fontSize: '28px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '12px' }}>
        Deal Redeemed!
      </h2>
      
      {redemptionResult && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
          style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
        >
          <Sparkles className="w-5 h-5" style={{ color: v5.colors.purple }} />
          <span style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.purple }}>
            +{redemptionResult.total_taps_earned} Taps earned!
          </span>
        </motion.div>
      )}
      
      {/* Redemption Code */}
      {redemptionResult?.redemption_code && (
        <div 
          className="rounded-lg p-4 mb-6"
          style={{ backgroundColor: v5.colors.navy }}
        >
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
            // show_to_staff
          </p>
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '28px', fontWeight: 700, color: v5.colors.white, letterSpacing: '0.2em' }}>
            {redemptionResult.redemption_code}
          </p>
        </div>
      )}
      
      <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted, marginBottom: '24px' }}>
        Show the code above to staff. Then let&apos;s show you how Tappd works!
      </p>
      
      <Button
        onClick={() => setStep(2)}
        className="rounded-xl px-6 py-5 text-base font-semibold"
        style={{
          background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
          color: v5.colors.white,
          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
        }}
      >
        How does Tappd work?
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </motion.div>
  );

  // Step 2: How Tappd works + Nearest venue
  const renderStep2 = () => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="text-center mb-8">
        <MapPin className="w-12 h-12 mx-auto mb-4" style={{ color: v5.colors.purple }} />
        <h2 style={{ fontFamily: v5.fonts.display, fontSize: '24px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '12px' }}>
          How Tappd Works
        </h2>
      </div>
      
      {/* How it works steps */}
      <div className="space-y-4 mb-8">
        <div className="flex items-start gap-4">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
          >
            <span style={{ fontFamily: v5.fonts.mono, fontWeight: 700, color: v5.colors.purple }}>1</span>
          </div>
          <div className="text-left">
            <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>
              Find a Venue Partner
            </p>
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted }}>
              Visit a bar, restaurant, or cafe with Tappd coasters
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
          >
            <span style={{ fontFamily: v5.fonts.mono, fontWeight: 700, color: v5.colors.purple }}>2</span>
          </div>
          <div className="text-left">
            <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>
              Scan a Coaster
            </p>
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted }}>
              Scan the QR code to unlock deals in that area
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-4">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
          >
            <span style={{ fontFamily: v5.fonts.mono, fontWeight: 700, color: v5.colors.purple }}>3</span>
          </div>
          <div className="text-left">
            <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>
              Claim & Redeem Deals
            </p>
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted }}>
              Browse local businesses and redeem exclusive deals
            </p>
          </div>
        </div>
      </div>
      
      {/* Nearest Venue Section */}
      <div 
        className="rounded-lg p-4"
        style={{ backgroundColor: v5.colors.cream }}
      >
        <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary, marginBottom: '12px' }}>
          Your Nearest Venue Partner
        </p>
        
        {gettingLocation && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: v5.colors.purple }} />
            <span style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>Getting your location...</span>
          </div>
        )}
        
        {loadingHub && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: v5.colors.purple }} />
            <span style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>Finding nearby venues...</span>
          </div>
        )}
        
        {locationError && (
          <div className="text-center py-4">
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginBottom: '12px' }}>
              {locationError}
            </p>
            <Button
              onClick={findNearestVenue}
              variant="outline"
              size="sm"
              className="rounded-lg"
              style={{
                border: `1px solid ${v5.colors.purple}`,
                color: v5.colors.purple
              }}
            >
              Try Again
            </Button>
          </div>
        )}
        
        {nearestHub?.hub && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-start gap-3 mb-3">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
              >
                <MapPin className="w-6 h-6" style={{ color: v5.colors.purple }} />
              </div>
              <div className="text-left">
                <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>
                  {nearestHub.hub.name}
                </p>
                <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted }}>
                  {nearestHub.hub.address}, {nearestHub.hub.city}
                </p>
                <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.purple, marginTop: '4px' }}>
                  {nearestHub.hub.distance_text} • {nearestHub.hub.deal_count} deals available
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => window.open(nearestHub.directions_url, '_blank')}
              className="w-full rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                color: v5.colors.white
              }}
            >
              <Navigation className="w-4 h-4 mr-2" />
              Get Directions
            </Button>
          </motion.div>
        )}
      </div>
      
      <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, textAlign: 'center', marginTop: '16px', marginBottom: '24px' }}>
        {businessName 
          ? `Visit ${nearestHub?.hub?.name || 'a venue partner'} to unlock deals from ${businessName} and more!`
          : `Visit ${nearestHub?.hub?.name || 'a venue partner'} to start unlocking local deals!`
        }
      </p>
      
      <Button
        onClick={onComplete}
        variant="outline"
        className="w-full rounded-xl py-5 text-base font-semibold"
        style={{
          border: `2px solid ${v5.colors.borderDefault}`,
          color: v5.colors.textPrimary,
          backgroundColor: 'transparent'
        }}
      >
        Got it, take me to Tappd
      </Button>
    </motion.div>
  );

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: v5.colors.cream }}>
      <div className="max-w-md mx-auto">
        {/* Always show Tappd logo */}
        <img src="/tappd-logo.png" alt="Tappd" className="mx-auto mb-8 h-12" />
        
        <div 
          className="rounded-xl p-8"
          style={{ 
            backgroundColor: v5.colors.white,
            border: `1px solid ${v5.colors.borderLight}`
          }}
        >
          {/* Progress dots - only show when there are 2 steps */}
          {showSuccessFirst && redemptionResult && (
            <div className="flex justify-center gap-2 mb-8">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: step === 1 ? v5.colors.purple : v5.colors.borderDefault }}
              />
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: step === 2 ? v5.colors.purple : v5.colors.borderDefault }}
              />
            </div>
          )}
          
          <AnimatePresence mode="wait">
            {step === 1 ? renderStep1() : renderStep2()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default NewUserOnboarding;
